import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, merge, of } from "rxjs";
import { map, shareReplay, switchMap, distinctUntilChanged, debounceTime } from "rxjs/operators";

import { EdhRepository } from "./edh.repository";
import { UmbracoHttpClient } from "../http";
import { AppserverInstallationCredentialsRepository } from "@e-forsyning/common/credentials";

@Injectable({ providedIn: "root" })
export class UlaesteDokumenterService {
    private readonly refreshTrigger = new BehaviorSubject<void>(null);
    private readonly ulaesteDokumenter$: Observable<number>;

    constructor(
        private readonly edhRepository: EdhRepository,
        private readonly umbracoHttp: UmbracoHttpClient,
        private readonly installationCredentials: AppserverInstallationCredentialsRepository
    ) {
        // Opret observable der reagerer på ejendomNr ændringer
        const ejendomNr$ = this.installationCredentials.valueChanges.pipe(
            map((credentials) => credentials?.ejendomNr),
            distinctUntilChanged()
        );

        // Kombiner både manuel refresh og ejendomNr ændringer
        const trigger$ = merge(this.refreshTrigger, ejendomNr$);

        this.ulaesteDokumenter$ = trigger$.pipe(
            // Debounce for at undgå for mange kald
            debounceTime(1000),
            // Tjek om brugeren er logget ind ved at se om der er en cryptId
            switchMap(() => {
                const credentials = this.installationCredentials.hent();
                if (!credentials?.cryptId) {
                    // Returner 0 hvis brugeren ikke er logget ind
                    return of(0);
                }

                // Hent Umbraco indstillinger for at få den faktiske antalAar værdi
                return this.umbracoHttp.hentSideData("mine-dokumenter").pipe(
                    switchMap((data: any) => {
                        const antalAar = data.antalAarTilbage ? parseInt(data.antalAarTilbage, 10) : -1;
                        return this.edhRepository.hentDokumenterForAktuelInstallation(antalAar);
                    }),
                    map((dokumenter) => dokumenter.filter((dokument) => !dokument.laest).length)
                );
            }),
            shareReplay(1)
        );
    }

    public hentAntalUlaesteDokumenter(): Observable<number> {
        return this.ulaesteDokumenter$;
    }

    public opdaterUlaesteDokumenter(): void {
        this.refreshTrigger.next(null);
    }

    /**
     * Starter observablen efter login - skal kaldes når brugeren logger ind
     */
    public startEfterLogin(): void {
        // Trigger en refresh når brugeren logger ind
        this.refreshTrigger.next(null);
    }
}
